import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { clsx } from 'clsx';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TODO:
 *  [1] Create custom focus trap? (https://github.com/theKashey/react-focus-lock/issues/225)
 *  [2] Add dinamic and animated mounting
 */

/**
 * This component is a low-level construct that is meant
 * to be used as an interaction blocker with the rest of the application
 * keeping it visible, requiring an action from the user to continue the app.
 *
 * Use it to create Dialogs or Drawers
 */
var Modal = function Modal(_ref) {
  var role = _ref.role,
    _ref$open = _ref.open,
    open = _ref$open === void 0 ? false : _ref$open,
    onDismiss = _ref.onDismiss,
    onEscape = _ref.onEscape,
    _ref$disableEscapeKey = _ref.disableEscapeKeyDown,
    disableEscapeKeyDown = _ref$disableEscapeKey === void 0 ? false : _ref$disableEscapeKey,
    _ref$disableBackdropC = _ref.disableBackdropClick,
    disableBackdropClick = _ref$disableBackdropC === void 0 ? false : _ref$disableBackdropC,
    _ref$disabledScrollLo = _ref.disabledScrollLock,
    disabledScrollLock = _ref$disabledScrollLo === void 0 ? false : _ref$disabledScrollLo,
    children = _ref.children,
    className = _ref.className;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isClient = _useState2[0],
    setIsClient = _useState2[1];
  var baseModalContentRef = useRef(null);
  useEffect(function () {
    setIsClient(true);
  }, []);
  useEffect(function () {
    var clickOutsideListener = function clickOutsideListener(event) {
      if (baseModalContentRef.current && !baseModalContentRef.current.contains(event.target) && onDismiss) {
        onDismiss();
      }
    };
    if (open && !disableBackdropClick) {
      document.addEventListener('mousedown', clickOutsideListener);
      document.addEventListener('touchstart', clickOutsideListener);
    } else {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    }
    return function () {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    };
  }, [disableBackdropClick, onDismiss, open]);

  // CHECK TODO [1]
  useEffect(function () {
    if (open) {
      var _baseModalContentRef$;
      (_baseModalContentRef$ = baseModalContentRef.current) === null || _baseModalContentRef$ === void 0 ? void 0 : _baseModalContentRef$.focus();
    }
  }, [open]);
  var handleKeyDown = function handleKeyDown(event) {
    var escapeKeyName = 'Escape';
    if (event.key === escapeKeyName) {
      event.stopPropagation();
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      if (open && !disableEscapeKeyDown && typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };
  if (!isClient || typeof document === 'undefined') {
    return null;
  }
  var mountNode = document.body;
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/_jsxs("div", {
    role: role,
    className: "tw-fixed tw-inset-0",
    style: {
      zIndex: '16000010',
      //* Increased from tw-z-dialog to this value due collisions with compass.ui/Drawer backdrop
      visibility: open ? 'visible' : 'hidden',
      opacity: open ? '1' : '0',
      transition: "opacity 150ms linear".concat(open ? '' : ', visibility 0s 150ms')
    },
    children: [/*#__PURE__*/_jsx("div", {
      className: clsx('tw-fixed tw-inset-0', 'tw-bg-neutral-dark-800'),
      style: {
        opacity: '55%',
        zIndex: '-1'
      },
      tabIndex: -1
    }), /*#__PURE__*/_jsx(FocusLock, {
      returnFocus: true,
      children: /*#__PURE__*/_jsx(RemoveScroll, {
        enabled: !disabledScrollLock && open,
        forwardProps: true,
        children: /*#__PURE__*/_jsx("div", {
          className: clsx('tw-z-1 tw-w-[100vw] tw-h-[100vh]', className),
          children: /*#__PURE__*/_jsx("div", {
            ref: baseModalContentRef,
            className: "focus:tw-outline-none tw-inline-block",
            onKeyDown: handleKeyDown,
            tabIndex: -1,
            children: children
          })
        })
      })
    })]
  }), mountNode);
};
export default Modal;